/**
 * Pure helpers for constructing and reasoning about Beats client-side.
 *
 * The server's `validator.ts` is the canonical Beat-construction site for
 * LLM-generated input — it handles JSON shape validation, clip lookup,
 * Uthana generation, and graceful degradation. The manual scene director
 * doesn't go through that path (it's a trusted producer running in the
 * same process as the player), but it still needs to emit Beats whose
 * shape and field semantics match what the validator produces. Otherwise
 * the BeatPlayer would behave subtly differently for the same logical
 * configuration depending on which path produced the Beat.
 *
 * This module mirrors the slim subset of `validator.ts` needed for the
 * two trusted-producer sources we expose in the manual UI:
 *
 *   - `face_only`  (no body clip; emotion / breath / holds / gaze drive
 *                   the figure)
 *   - `library`    (an existing catalog clip)
 *
 * Uthana sources are intentionally NOT mirrored here — they require the
 * server (network, credits, caching). When the manual UI wants Uthana
 * playback, it would have to round-trip the server like the LLM path.
 *
 * KEEP IN SYNC with server/src/validator.ts:
 *   - clamp01() ↔ clamp01() (lines ~38–44)
 *   - materialiseFaceOnlyBeat() ↔ materialiseFaceOnly() (lines ~190–218)
 *   - materialiseLibraryBeat() ↔ materialise() (lines ~104–136)
 *
 * If you change clamp ranges, default fallbacks, or which fields propagate
 * for a given source, update both — the comment above each helper tells
 * you which server function it pairs with.
 */

import type { Beat, HoldSlot } from './types';
import type {
  Emotion,
  Gesture,
  GazeDirection,
  BodyGesture,
  BodyState,
} from './vocab';
import { ALL_PROCEDURAL_HOLDS, type ProceduralHoldName } from '../motion/body-profiles';
import { tempoForEmotion } from './emotion-tempo';

const PROCEDURAL_HOLD_SET: ReadonlySet<string> = new Set(ALL_PROCEDURAL_HOLDS);

// Default beat-time when no emotion is set and no explicit duration is given.
// Keeps the legacy 5s baseline as the floor for the rare unemotional beat.
// Emotion-driven defaults override this via `tempoForEmotion` (see usage in
// `materialiseBeatFromInput`).
const DEFAULT_FACE_DURATION_SEC = 5;

// ── Numeric clamp ────────────────────────────────────────────────────────

/**
 * Clamp `v` to [0,1], returning `fallback` when the value is missing or
 * non-numeric. Mirrors the server validator's `clamp01` so the manual
 * path produces identical numeric behaviour for partial inputs.
 */
export function clamp01(v: unknown, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(1, v));
}

// ── Subset of clip metadata the materialiser needs ───────────────────────
//
// We don't import the server's full `ClipMetadata` to avoid a cross-project
// type leak; the manual UI fetches the catalog from `GET /catalog` and
// passes the bits we need. Keep this aligned with `ClipMetadata` in
// `server/src/types.ts`.
export interface ClipMetadataLite {
  name: string;
  url: string;
  loopable: boolean;
  symmetric: boolean;
  is_idle: boolean;
  emotion: Emotion;
  intensity: number;
}

// ── Manual-input shape (per-beat in the panel) ───────────────────────────
//
// One unified shape. The presence of `clip` is the only thing that decides
// whether the runtime gets a body clip (`source: 'library'`) or no body
// clip change (`source: 'face_only'`). Every other field — emotion, holds,
// gaze, gestures, body state, smoothing — applies regardless. Treating
// `source` as a discriminator was an accidental UI artefact; the runtime
// already drives face/holds/state alongside any body source.
export interface ManualBeatInput {
  /**
   * Optional catalog clip name. When set, this beat plays the clip as
   * its body animation (becomes a `library` beat at runtime). When
   * omitted, the body keeps doing whatever it was doing (face_only).
   * Either way, every other field below is honoured.
   */
  clip?: string;
  /**
   * Total beat time in seconds. Acts as a floor: the clip plays once,
   * clamps on its last frame, and the figure dwells in that pose until
   * duration_sec elapses (or until the clip's natural duration if
   * longer). `null` / undefined means "use the clip's natural duration
   * with zero dwell" for library beats; face_only beats default to
   * DEFAULT_FACE_DURATION_SEC.
   */
  duration_sec?: number | null;
  reason?: string;
  emotion?: Emotion;
  intensity?: number;
  energy?: number;
  /** Procedural hold names (no GLB / Uthana support in manual). */
  holds?: string[];
  release_holds?: string[];
  gaze?: GazeDirection | null;
  gesture?: Gesture | null;
  body_gesture?: BodyGesture | null;
  body_state?: BodyState | null;
  /** Override the auto-chosen body crossfade. */
  fade_sec?: number;
  /** Override the default 0.55s procedural-hold attack for THIS beat's holds. */
  hold_attack_sec?: number;
  /** Override the default 0.5s procedural-hold release for THIS beat's release_holds. */
  hold_release_sec?: number;
}

// ── Hold resolution (procedural-only on the manual path) ─────────────────
//
// The manual UI exposes only procedural holds (no Uthana). resolveHolds
// on the server has to round-trip GLB downloads; we don't. This filters
// to procedural holds and assembles `HoldSlot`s with empty URL (the
// client's setHold dispatch checks `isProceduralHold(name)` first and
// never uses url for those — the GLB url is only relevant for legacy
// Uthana-backed holds).
function resolveProceduralHolds(
  names: string[] | undefined,
  attackSecResolved: number | undefined,
): HoldSlot[] {
  if (!names || names.length === 0) return [];
  const out: HoldSlot[] = [];
  for (const name of names) {
    if (!PROCEDURAL_HOLD_SET.has(name)) {
      console.warn(`[beat-utils] dropping non-procedural hold "${name}" (manual path is procedural-only)`);
      continue;
    }
    // Procedural holds are postures: they're either committed (1.0) or
    // not active. The runtime envelope handles the smooth attack/release
    // shape; mid-sustain values would just blend mixer-pose into the
    // calibrated pose and look broken. Transitions between two poses
    // are achieved by activating the new hold (which auto-releases the
    // old one via limb-conflict resolution), with attack_sec /
    // hold_release_sec controlling the crossfade duration.
    //
    // attackSecResolved is the FINAL value (input override OR tempo
    // default). The materialiser passes the resolved value here so the
    // HoldSlot always carries an explicit number — downstream code
    // (BeatPlayer.applyHoldsForBeat) doesn't have to reconcile beat-level
    // and hold-level fallbacks.
    const slot: HoldSlot = { name, url: '', weight: 1.0 };
    if (attackSecResolved != null) slot.attack_sec = attackSecResolved;
    out.push(slot);
  }
  return out;
}

// ── Unified materialiser ─────────────────────────────────────────────────
//
// One function, one mental model: build a Beat from the manual input.
// EVERY field that influences the face / state / holds layer is applied
// unconditionally. The presence of `clip` is the ONLY thing that decides
// whether a body animation plays — and even then, all the other fields
// still apply on top of it.
//
// Differences from the server validator (these are deliberate fixes
// that should be mirrored back to the LLM path later):
//
//   - The server's library path overrides the user's emotion with the
//     catalog's emotion. The manual path treats the user as authoritative;
//     catalog values are used only as fallbacks when the user left a
//     field blank. (If you're hand-directing, you mean what you set.)
//   - The server's library path discards `holds`/`release_holds` (a quirk
//     that predates the manual UI). The manual path applies holds
//     regardless of whether a body clip is present — "play `Looking
//     Around` AND keep arms_crossed active" is a perfectly valid beat.
//     The runtime already handles this: BeatPlayer.applyHoldsForBeat
//     doesn't branch on source.
export function materialiseBeatFromInput(
  input: ManualBeatInput,
  catalogLookup: (clipName: string) => ClipMetadataLite | undefined,
): Beat {
  const meta = input.clip ? catalogLookup(input.clip) : undefined;
  if (input.clip && !meta) {
    throw new Error(`clip "${input.clip}" not in catalog`);
  }

  // Catalog values feed *fallbacks* only — the user's explicit values
  // always win. If the user left emotion blank and a clip is present,
  // borrow the catalog's emotion (curated). Otherwise default to neutral.
  const emotion: Emotion = input.emotion ?? meta?.emotion ?? 'neutral';
  const intensityFallback = meta?.intensity ?? 0.6;
  const energyFallback = meta ? meta.intensity * 0.8 + 0.1 : 0.5;
  const intensity = clamp01(input.intensity, intensityFallback);

  // Resolve emotion-driven smoothing/duration defaults. Used for any of
  // fade_sec / hold_attack_sec / hold_release_sec / duration_sec that the
  // user didn't override. See `emotion-tempo.ts` for the table — a sad
  // beat gets ~2.5s fade and ~12s duration; an angry beat gets ~0.8s
  // fade and ~4s duration. Intensity lerps between the table value and
  // the neutral baseline so a faint expression doesn't carry the full
  // tempo weight.
  const tempo = tempoForEmotion(emotion, intensity);

  // Resolved smoothing values. `??` here is intentional: both `null` and
  // `undefined` for hold_attack_sec / hold_release_sec / fade_sec mean
  // "use the emotion default" — the only way to opt out of smoothing
  // is to pass a concrete number. duration_sec uses a stricter check
  // (see below) because `null` carries explicit meaning for library beats.
  const fadeResolved = input.fade_sec ?? tempo.fade_sec;
  const holdAttackResolved = input.hold_attack_sec ?? tempo.hold_attack_sec;
  const holdReleaseResolved = input.hold_release_sec ?? tempo.hold_release_sec;

  // Holds applied to every beat — the runtime doesn't care about source.
  // Procedural-only on the manual path; resolveProceduralHolds warns
  // and drops anything not in the procedural catalog. Pass the RESOLVED
  // attack value so each HoldSlot carries the emotion-driven default
  // (or the user's override).
  const holds = resolveProceduralHolds(input.holds, holdAttackResolved);

  // Body-clip vs face-only branching. Beats are always one-shot now;
  // duration_sec is the dwell-floor (see ManualBeatInput.duration_sec).
  //   - face_only beats: no body clip change. duration_sec defaults to
  //     the emotion-driven tempo (sadness → ~12s, surprise → ~2.5s),
  //     falling back to DEFAULT_FACE_DURATION_SEC only if tempo lookup
  //     somehow returns NaN. The figure stays on whatever it was last
  //     clamped on while face/holds/state run.
  //   - library beats: clip plays once, clamps on its last frame.
  //     duration_sec acts as a floor; the user can pass `null`
  //     EXPLICITLY to opt out of the floor (advance at clip's natural
  //     end with zero dwell). `undefined` defaults to tempo so a sad
  //     library beat lingers on its last frame after the clip ends.
  let source: 'face_only' | 'library';
  let clipName: string;
  let url: string;
  let duration_sec: number | null;
  let symmetric: boolean;
  let is_idle: boolean;
  let loopable: boolean;

  if (meta) {
    source = 'library';
    clipName = meta.name;
    url = meta.url;
    // Distinguish `undefined` (apply emotion default) from `null`
    // (explicit "no floor"). Property absence in object literals reads
    // as undefined, which matches the manual UI's "blank field" state.
    if (input.duration_sec === undefined) {
      duration_sec = tempo.duration_sec;
    } else {
      duration_sec = input.duration_sec;
    }
    symmetric = meta.symmetric;
    is_idle = meta.is_idle;
    loopable = meta.loopable;
  } else {
    source = 'face_only';
    clipName = `face:${emotion}`;
    url = '';
    // face_only has no clip, so `null` would mean "advance immediately"
    // which is never useful — fall back to the emotion default in both
    // the undefined and null cases.
    duration_sec = input.duration_sec ?? tempo.duration_sec ?? DEFAULT_FACE_DURATION_SEC;
    symmetric = false;
    is_idle = true;
    loopable = false;
  }

  const beat: Beat = {
    source,
    clip: clipName,
    url,
    duration_sec,
    reason: input.reason ?? '',
    symmetric,
    is_idle,
    loopable,
    emotion,
    intensity,
    energy: clamp01(input.energy, energyFallback),
    holds: holds.length > 0 ? holds : undefined,
    release_holds:
      input.release_holds && input.release_holds.length > 0
        ? [...input.release_holds]
        : undefined,
    gaze: input.gaze ?? undefined,
    gesture: input.gesture ?? undefined,
    body_gesture: input.body_gesture ?? undefined,
    body_state: input.body_state ?? undefined,
    // Always emit resolved smoothing values. The runtime previously
    // treated absence as "use the player default"; with emotion-driven
    // tempo we always have a concrete answer, and surfacing it makes
    // the Beat self-describing (debug overlay reads beat.fade_sec, etc.).
    fade_sec: fadeResolved,
    hold_release_sec: holdReleaseResolved,
  };
  return beat;
}

// ── Back-compat aliases ─────────────────────────────────────────────────
// Older callers that asked for the split materialisers route through
// the unified one. New code should call `materialiseBeat` directly.
export function materialiseFaceOnlyBeat(input: ManualBeatInput): Beat {
  return materialiseBeatFromInput({ ...input, clip: undefined }, () => undefined);
}
export function materialiseLibraryBeat(
  input: ManualBeatInput,
  meta: ClipMetadataLite,
): Beat {
  return materialiseBeatFromInput(
    { ...input, clip: meta.name },
    (n) => (n === meta.name ? meta : undefined),
  );
}

// ── Cumulative state walker ──────────────────────────────────────────────
//
// Returns the (active holds set, active body_state) going INTO beat
// `index`. Folds beats[0..index-1] applying each beat's release_holds
// and holds in order, and tracking the most recent non-null body_state.
//
// Used by the manual UI to display "what's the figure standing in
// going INTO this beat?" — invaluable when the user has 8 beats and
// can't remember what they activated 5 rows up.
//
// O(n) per call; cheap enough to recompute on every render.
export interface CumulativeState {
  /** Hold names active when this beat starts. */
  holds: ReadonlySet<string>;
  /** body_state inherited from the most recent non-null setting. */
  body_state: BodyState;
}

export function cumulativeHoldState(
  inputs: readonly ManualBeatInput[],
  index: number,
): CumulativeState {
  const holds = new Set<string>();
  let body_state: BodyState = 'neutral_idle';
  const stop = Math.min(index, inputs.length);
  for (let i = 0; i < stop; i += 1) {
    const b = inputs[i];
    if (b.release_holds) {
      for (const r of b.release_holds) holds.delete(r);
    }
    if (b.holds) {
      for (const h of b.holds) holds.add(h);
    }
    if (b.body_state) body_state = b.body_state;
  }
  return { holds, body_state };
}

/**
 * Public entry point for the manual director: turn a manual beat input
 * into a runtime Beat. Routes to the unified materialiser; presence of
 * `clip` is what decides face_only vs library.
 */
export function materialiseBeat(
  input: ManualBeatInput,
  catalogLookup: (clipName: string) => ClipMetadataLite | undefined,
): Beat {
  return materialiseBeatFromInput(input, catalogLookup);
}

/**
 * Detect procedural-hold limb conflicts inside a single beat's `holds`
 * array (e.g. arms_crossed + hand_in_pocket_right both claim the right
 * arm). Returns the names that conflict with an earlier entry in the
 * same array. Used by the panel to render an inline warning before
 * the user attempts to play a self-conflicting scene.
 *
 * For cross-beat conflict awareness use cumulativeHoldState() above.
 */
export { conflictingHolds } from '../motion/body-profiles';
export type ProceduralHoldNameRef = ProceduralHoldName;
